// Renders stitch SVGs onto canvas by converting SVG text → Image → drawImage
// Color replacement: swap black strokes/fills with the user's chosen color
import { colorizeSvg } from '../utils/svgColorize';

// Default scale if none provided
const DEFAULT_SCALE = 1.5;

// Parsed native sizes from SVG viewBox/width/height
const svgNativeSizes: Record<string, { width: number; height: number }> = {};

// Cache: "variantId:color" → loaded Image (avoids re-creating per frame)
const MAX_CACHE_SIZE = 500;
const imageCache: Record<string, HTMLImageElement> = {};
const imageCacheKeys: string[] = []; // FIFO order
const pendingLoads: Record<string, Promise<HTMLImageElement>> = {};

// Callback to trigger canvas repaint when SVGs finish loading
let onSvgReady: (() => void) | null = null;
export function setOnSvgReady(cb: () => void) { onSvgReady = cb; }

// SVG source text cache: variantId → raw SVG string
const svgSourceCache: Record<string, string> = {};

/**
 * Register an SVG source for a variant. Called once at load time.
 * We fetch the SVG text from its URL so we can do color replacement.
 */
function parseSvgSize(svgText: string): { width: number; height: number } | null {
  // Try viewBox first: viewBox="0 0 W H"
  const vbMatch = svgText.match(/viewBox="([^"]+)"/);
  if (vbMatch) {
    const parts = vbMatch[1].trim().split(/[\s,]+/).map(Number);
    if (parts.length === 4 && parts[2] > 0 && parts[3] > 0) {
      return { width: parts[2], height: parts[3] };
    }
  }
  // Fallback to width/height attributes (strip units like "mm")
  const wMatch = svgText.match(/width="([\d.]+)/);
  const hMatch = svgText.match(/height="([\d.]+)/);
  if (wMatch && hMatch) {
    return { width: parseFloat(wMatch[1]), height: parseFloat(hMatch[1]) };
  }
  return null;
}

export function registerSvgSource(variantId: string, svgUrl: string): void {
  if (!svgUrl) return;
  fetch(svgUrl)
    .then(r => r.text())
    .then(text => {
      if (text.includes('<svg')) {
        svgSourceCache[variantId] = text;
        const size = parseSvgSize(text);
        if (size) svgNativeSizes[variantId] = size;
        if (onSvgReady) onSvgReady();
      }
    })
    .catch(() => {});
}

const colorize = colorizeSvg;

function getImage(variantId: string, color: string): HTMLImageElement | null {
  const key = `${variantId}:${color}`;

  // Return cached image if ready
  if (imageCache[key]?.complete) {
    return imageCache[key];
  }

  // Start loading if we have source and haven't started yet
  const svgText = svgSourceCache[variantId];
  if (!svgText || key in pendingLoads) return null;

  const colorized = colorize(svgText, color);
  const blob = new Blob([colorized], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);

  const img = new Image();
  pendingLoads[key] = new Promise<HTMLImageElement>((resolve) => {
    img.onload = () => {
      // Evict oldest entry if cache is full
      if (imageCacheKeys.length >= MAX_CACHE_SIZE) {
        const oldest = imageCacheKeys.shift()!;
        delete imageCache[oldest];
      }
      imageCache[key] = img;
      imageCacheKeys.push(key);
      URL.revokeObjectURL(url);
      delete pendingLoads[key];
      resolve(img);
      if (onSvgReady) onSvgReady();
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      delete pendingLoads[key];
      resolve(img);
    };
  });
  img.src = url;

  return null; // Not ready yet, will be drawn on next canvas redraw
}

/**
 * Draw a stitch SVG onto the canvas at the current transform origin.
 * The canvas should already have translate(x,y) and rotate(angle) applied.
 * Returns true if the SVG was drawn, false if not available (fallback needed).
 */
export function drawSvgStitch(
  ctx: CanvasRenderingContext2D,
  variantId: string,
  color: string,
  scale?: number
): boolean {
  const img = getImage(variantId, color);
  if (!img) return false;

  const s = scale || DEFAULT_SCALE;
  const native = svgNativeSizes[variantId];
  const w = native ? native.width * s : 24;
  const h = native ? native.height * s : 24;
  ctx.globalAlpha = 0.85;
  ctx.drawImage(img, -w / 2, -h / 2, w, h);
  return true;
}

/**
 * Check if an SVG source is available for a variant.
 */
export function hasSvgSource(variantId: string): boolean {
  return variantId in svgSourceCache;
}

/**
 * Pre-load SVG images for a set of variant IDs at a given color.
 * Returns a Promise that resolves when all images are ready.
 */
export function preloadImages(variantIds: string[], color: string): Promise<void> {
  const promises: Promise<unknown>[] = [];
  for (const vid of variantIds) {
    const key = `${vid}:${color}`;
    if (imageCache[key]?.complete) continue;
    // Trigger load
    getImage(vid, color);
    if (key in pendingLoads) {
      promises.push(pendingLoads[key]);
    }
  }
  return Promise.all(promises).then(() => {});
}
